/**
 * Express Pay A/B Experiment Module
 *
 * Fetches the "express-pay" variant from Amplitude Experiment,
 * sets a cart attribute for treatment users, and exposes a function to
 * send the exposure event on add-to-cart.
 */

import { setCartAttributes } from "@theme/utilities";
import { createAmplitudeExperiment } from "@theme/experiment.util";

const namespace = "[ExpressPayExperiment]";
const FLAG_KEY = "express-pay";
const CART_ATTRIBUTE_KEY = "express-pay-experiment";

/**
 * Read the value already stored in cart attributes, which Liquid renders onto
 * this module's script tag.
 *
 * @returns {string} The current attribute value, or an empty string if unset.
 */
function getExistingCartAttribute() {
  const scriptElement = document.querySelector("script[data-express-pay-experiment]");
  const raw =
    scriptElement instanceof HTMLScriptElement ? scriptElement.dataset.expressPayExperiment : null;

  if (!raw || raw === "null") return "";

  return raw;
}

const experiment = createAmplitudeExperiment({
  flagKey: FLAG_KEY,
  namespace,
  bodiExperimentsKey: "expressPayExperiment",
  onVariantResolved: async (resolvedVariant) => {
    // An unresolved variant is not an assignment to control. Leave the attribute
    // as-is so a flaky fetch can't strip a shopper out of treatment mid-session.
    if (!resolvedVariant) {
      console.warn(`${namespace} - Variant unresolved, leaving cart attribute unchanged`);
      return;
    }

    const attributeValue = resolvedVariant === "treatment" ? "treatment" : "";

    if (attributeValue === getExistingCartAttribute()) {
      console.info(`${namespace} - Cart attribute already '${attributeValue}', skipping update`);
      return;
    }

    await setCartAttributes({ [CART_ATTRIBUTE_KEY]: attributeValue });
    console.info(`${namespace} - Cart attribute set to '${attributeValue}'`);
  },
});

/**
 * Returns the resolved variant value for the express-pay-experiment flag.
 * Waits for initialization if it hasn't completed yet.
 *
 * @returns {Promise<string | null>}
 */
export async function getExpressPayExperimentVariant() {
  return experiment.getVariant();
}

/**
 * Send the exposure event for the express-pay-experiment flag.
 * Should be called when the user adds an item to cart.
 *
 * @returns {Promise<void>}
 */
export async function sendExpressPayExposure() {
  return experiment.sendExposure();
}
